import { Box, Button, Flex, Grid, GridItem, IconButton, Text, useToast, VStack } from "@chakra-ui/react";
import { Fragment, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { dimensionsKeys, showErrorToast } from "../../../../utils";
import { useTranslation } from "react-i18next";
import { DownloadSimple, FileArrowUp, PencilSimpleLine, PencilSimple, Trash, UploadSimple } from "@phosphor-icons/react";
import { EsgLabel } from "../../../../components/Forms";
import EmissionsItemModal from "./EmissionsItemModal";
import { isOrganizationStructureReady } from "./organizationStructureReady";
import FileDragDrop from "../../../../components/FileDragDrop";
import {
  manualEmissionItemKeys,
  useCreateManualEmissionItems,
  useDeleteManualEmissionItem,
  useGetManualEmissionItems,
  useUpdateManualEmissionItem,
} from "../../../../hooks/query/questionnaire/manual-emission-item";

const NUMBER_OF_EMISSIONS_STATIC_COLS = 1; // tCO2e
const NUMBER_OF_ACTION_COLS = 1;

// Flattens backend DTO ({ id, tco2e, dimensionItems: { dimensionOneItem, ... } }) to the row shape
// the grid + modal use ({ id, tco2e, dimensionOneItem, ... }).
const flattenItem = (apiItem) => {
  const row = { id: apiItem.id, tco2e: apiItem.tco2e, notes: apiItem.notes };
  dimensionsKeys.forEach((key) => {
    row[`${key}Item`] = apiItem.dimensionItems?.[`${key}Item`] ?? null;
  });
  return row;
};

// Inverse of flattenItem — assembles the DTO body for create/update.
const buildDto = (formRow, scopeCategoryKey) => {
  const dimensionItems = {};
  dimensionsKeys.forEach((key) => {
    dimensionItems[`${key}Item`] = formRow[`${key}Item`] || null;
  });
  return {
    scopeCategoryKey,
    tco2e: formRow.tco2e === "" || formRow.tco2e == null ? null : Number(formRow.tco2e),
    notes: formRow.notes ?? null,
    dimensionItems,
  };
};

function EmissionsDataGrid({
  organizationStructure,
  dimensionItems,
  isPreview,
  questionnaireId,
  scopeCategoryKey,
  onDownloadTemplate,
  importBtnId,
  onImport,
}) {
  const { t } = useTranslation("translation");
  const toast = useToast();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [activeMethod, setActiveMethod] = useState("direct");
  const [templateFile, setTemplateFile] = useState(null);
  const fileInputRef = useRef(null);

  const { data: apiItems = [] } = useGetManualEmissionItems(
    { questionnaireId, scopeCategoryKey },
    { enabled: !!questionnaireId && !!scopeCategoryKey }
  );

  const items = useMemo(() => apiItems.map(flattenItem), [apiItems]);

  const totalTco2e = useMemo(() => {
    return items.reduce((sum, item) => {
      const n = Number(item.tco2e);
      return Number.isFinite(n) ? sum + n : sum;
    }, 0);
  }, [items]);

  const invalidate = () => queryClient.invalidateQueries(manualEmissionItemKeys.byScope(questionnaireId, scopeCategoryKey));
  const onMutationError = (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);

  const createMutation = useCreateManualEmissionItems({ onSuccess: invalidate, onError: onMutationError });
  const updateMutation = useUpdateManualEmissionItem({ onSuccess: invalidate, onError: onMutationError });
  const deleteMutation = useDeleteManualEmissionItem({ onSuccess: invalidate, onError: onMutationError });

  if (isPreview && items.length === 0) {
    return null;
  }

  // Placed after every hook, per the no-early-returns-before-hooks rule, and before the first read of
  // `organizationStructure.dimensions` below. Without it this component threw "undefined is not an object
  // (evaluating 't.dimensions')" and took the whole task page down to a blank screen whenever it mounted
  // before the org-structure query resolved. React Query re-renders when the data lands, so the grid
  // appears a moment later rather than never — see organizationStructureReady for why this is a guard and
  // not optional chaining.
  if (!isOrganizationStructureReady(organizationStructure)) {
    return null;
  }

  const activeDimensions = dimensionsKeys.filter(
    (dimKey) => organizationStructure.dimensions[dimKey] !== null
  );

  const colCount =
    (activeDimensions.length + NUMBER_OF_EMISSIONS_STATIC_COLS) * 2 +
    (!isPreview ? NUMBER_OF_ACTION_COLS : 0);

  const getDimensionItemLabel = (dimKey, value) => {
    return organizationStructure.dimensions[dimKey]?.items?.find(
      (el) => el.id === value || el.id?.toString() === value?.toString()
    )?.name ?? "";
  };

  const handleAddClick = () => {
    setEditData(null);
    setModalOpen(true);
  };

  const handleEditClick = (item) => {
    setEditData(item);
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    deleteMutation.mutate({ questionnaireId, id });
  };

  const handleModalSubmit = (formData) => {
    const newRows = formData.emissionsItems ?? [];
    if (editData) {
      const dto = buildDto(newRows[0], scopeCategoryKey);
      updateMutation.mutate({ questionnaireId, id: editData.id, data: dto });
    } else {
      const data = newRows.map((row) => buildDto(row, scopeCategoryKey));
      createMutation.mutate({ questionnaireId, data });
    }
    setModalOpen(false);
    setEditData(null);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditData(null);
  };

  const headerTitles = activeDimensions.map(
    (dimKey) => organizationStructure.dimensions[dimKey].name
  );
  headerTitles.push("tCO2e");
  if (!isPreview) headerTitles.push("");

  return (
    <Fragment>
      {!isPreview && (
        <Flex gap={2} mb={4} align="center" flexWrap="wrap">
          <Button
            size="sm"
            variant="outline"
            borderRadius="full"
            borderColor={activeMethod === "direct" ? "lime" : "limeShade.600"}
            bg={activeMethod === "direct" ? "lightLime" : "transparent"}
            color="limeShade.600"
            leftIcon={<PencilSimpleLine size={14} />}
            onClick={() => { setActiveMethod("direct"); handleAddClick(); }}
            _hover={{ bg: "lightLime", borderColor: "lime" }}
          >
            + {t("buttons.addNewItems")}
          </Button>
          {onImport && (
          <Button
            size="sm"
            variant="outline"
            borderRadius="full"
            borderColor={activeMethod === "import" ? "lime" : "limeShade.600"}
            bg={activeMethod === "import" ? "lightLime" : "transparent"}
            color="limeShade.600"
            leftIcon={<FileArrowUp size={14} />}
            onClick={() => setActiveMethod(activeMethod === "import" ? "direct" : "import")}
            _hover={{ bg: "lightLime", borderColor: "lime" }}
          >
            Import from Template
          </Button>
          )}
          {/* No AI Mapping button here: manual tCO2e entry has no AiIngestTarget: SCOPE_1 cannot be narrowed to a
          stationary/mobile/refrigerant group, and Scope 3 has no target at all.
              Adding one means new enum values and backend handlers, not a frontend change. */}
        </Flex>
      )}
      {activeMethod === "import" && !isPreview && (
        <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={6} mb={4} bg="gray.50">
          <Text fontSize="14px" fontWeight="600" color="gray.700" mb={1}>Import from Template</Text>
          <Text fontSize="13px" color="gray.500" mb={4}>Download a template, fill in your data, then drag and drop or select the file to import.</Text>
          <FileDragDrop onFiles={(files) => { if (files?.[0]) setTemplateFile(files[0]); }} borderRadius="lg">
            <VStack
              border="2px dashed"
              borderColor={templateFile ? "lime" : "gray.300"}
              borderRadius="lg"
              py={6}
              px={4}
              spacing={2}
              cursor="pointer"
              bg={templateFile ? "lightLime" : "white"}
              _hover={{ borderColor: "lime", bg: "lightLime" }}
              transition="all 0.15s"
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadSimple size={28} color={templateFile ? "#85C22A" : "#A0AEC0"} />
              {templateFile ? (
                <Text fontSize="13px" fontWeight="600" color="gray.700">{templateFile.name}</Text>
              ) : (
                <>
                  <Text fontSize="13px" fontWeight="600" color="gray.600">Drop file here or click to browse</Text>
                  <Text fontSize="11px" color="gray.400">.xlsx, .xls</Text>
                </>
              )}
            </VStack>
          </FileDragDrop>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".xlsx,.xls"
            onChange={(e) => {
              if (e.target.files?.[0]) setTemplateFile(e.target.files[0]);
              e.target.value = null;
            }}
          />
          <Flex justify="space-between" align="center" mt={3}>
            {onDownloadTemplate ? (
              <Button
                size="sm"
                variant="outline"
                borderRadius="full"
                borderColor="limeShade.600"
                color="limeShade.600"
                leftIcon={<DownloadSimple size={14} />}
                onClick={onDownloadTemplate}
              >
                Download Template
              </Button>
            ) : <Box />}
            {templateFile && (
              <Flex gap={2}>
                <Button
                  size="sm"
                  variant="ghost"
                  color="gray.400"
                  onClick={() => setTemplateFile(null)}
                  _hover={{ color: "gray.600" }}
                >
                  Remove
                </Button>
                <Button
                  size="sm"
                  bg="lime"
                  color="pureBlack"
                  borderRadius="full"
                  leftIcon={<UploadSimple size={14} />}
                  _hover={{ bg: "limeShade.300" }}
                  onClick={() => {
                    if (onImport) {
                      const fakeEvent = { preventDefault: () => {}, target: { files: [templateFile] } };
                      onImport(fakeEvent, importBtnId);
                    }
                    setTemplateFile(null);
                  }}
                >
                  Import
                </Button>
              </Flex>
            )}
          </Flex>
        </Box>
      )}

      {items.length > 0 && (
        <Box borderWidth="1px" borderRadius="md" overflow="hidden" bg="white" mb={4}>
          <Grid
            templateColumns={`repeat(${colCount}, 1fr)`}
            minWidth="700"
            maxHeight="400"
            overflow="auto"
            sx={{
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
              "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
              scrollbarWidth: "thin",
              scrollbarColor: "transparent transparent",
              "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
            }}
          >
            {/* Header */}
            {headerTitles.map((title) => (
              <GridItem
                key={`heading-emissions-${title}`}
                px={4}
                py={3}
                display="flex"
                alignItems="center"
                bg="gray.50"
                borderBottom="1px solid"
                borderColor="gray.200"
                colSpan={isPreview ? 2 : title !== "" ? 2 : 1}
              >
                <Text
                  fontSize="13px"
                  fontWeight="600"
                  color="gray.500"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {title}
                </Text>
              </GridItem>
            ))}

            {/* Rows */}
            {items.map((item) => {
              const totalCols =
                activeDimensions.length + NUMBER_OF_EMISSIONS_STATIC_COLS + (!isPreview ? NUMBER_OF_ACTION_COLS : 0);

              const cells = [];

              // Dimension cells
              activeDimensions.forEach((dimKey) => {
                const val = getDimensionItemLabel(dimKey, item[`${dimKey}Item`]);
                cells.push(
                  <EsgLabel overflow="hidden" fontWeight="normal" color="gray.700" text={val || "—"} title={val} />
                );
              });

              // tCO2e cell
              const display = item.tco2e !== "" && item.tco2e != null ? item.tco2e : "—";
              cells.push(
                <EsgLabel overflow="hidden" fontWeight="normal" color="gray.700" text={display} title={display} />
              );

              // Action cell
              if (!isPreview) {
                cells.push(
                  <Flex>
                    <IconButton
                      icon={<PencilSimple size={18} />}
                      variant="ghost"
                      color="gray.500"
                      _hover={{ color: "limeShade.600" }}
                      onClick={() => handleEditClick(item)}
                      aria-label="Edit"
                      size="sm"
                    />
                    <IconButton
                      icon={<Trash size={18} />}
                      variant="ghost"
                      color="gray.500"
                      _hover={{ color: "red.500" }}
                      onClick={() => handleDelete(item.id)}
                      aria-label="Delete"
                      size="sm"
                    />
                  </Flex>
                );
              }

              return cells.map((cell, cellIndex) => (
                <GridItem
                  key={`${item.id}-${cellIndex}`}
                  px={4}
                  py={2}
                  display="flex"
                  alignItems="center"
                  borderBottom="1px solid"
                  borderColor="gray.100"
                  colSpan={isPreview ? 2 : cellIndex === totalCols - 1 ? 1 : 2}
                >
                  {cell}
                </GridItem>
              ));
            })}

            <GridItem
              gridColumn="1 / -1"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              px={4}
              py={2}
              bg="lightLime"
              borderTop="2px solid"
              borderColor="gray.200"
            >
              <Text fontSize="13px" fontWeight="600" color="gray.600">Total</Text>
              <Text fontSize="13px" fontWeight="700" color="gray.800">
                {totalTco2e.toLocaleString("en-GB", { maximumFractionDigits: 2 })} tCO2e
              </Text>
            </GridItem>
          </Grid>
        </Box>
      )}

      {items.length === 0 && (
        <Box
          border="1px dashed"
          borderColor="gray.300"
          borderRadius="md"
          py={8}
          mb={4}
          textAlign="center"
        >
          <Text fontSize="14px" color="gray.400">
            No emissions data added yet.{!isPreview && " Click \"+ Add New Items\" to get started."}
          </Text>
        </Box>
      )}

      {/* Add/Edit Modal */}
      {modalOpen && (
        <EmissionsItemModal
          modalData={{
            organizationStructure,
            dimensionItems,
            editData,
          }}
          isOpen={modalOpen}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
        />
      )}
    </Fragment>
  );
}

export default EmissionsDataGrid;
